// ─────────────────────────────────────────────────────────────────────────────
//  Themes — chosen by the host, applied to every screen in the room.
//
//  A theme is not a palette swap. It carries type, corner radius, shadow,
//  surface treatment and an optional texture, because "Retro" and "Clay" are
//  not the same design in different colours — they are different designs. Every
//  value lands as a CSS custom property, so switching costs one write to
//  <html> and no re-render.
//
//  Themes may be light or dark: `--c-ink` is the text colour and every surface
//  is expressed as an alpha of it, so the whole system inverts correctly.
// ─────────────────────────────────────────────────────────────────────────────

export interface ThemeVars {
  /* colour ------------------------------------------------------------- */
  "--c-board": string;
  "--c-board-dark": string;
  "--c-accent": string;
  /** The accent as *text on the page background*. On a dark theme that is the
      lighter tint; on a light theme it has to be the darker shade, or the
      headings vanish. */
  "--c-accent-light": string;
  /** Text colour for numbers and labels sitting on a board tile — must
      contrast with --c-board, which is a different problem from the above. */
  "--c-on-board": string;
  "--c-ink": string;
  "--c-on-accent": string;
  /* the page background — a full CSS background value, so a theme can use a
     gradient, a flat colour, or a pattern */
  "--bg": string;
  /* semantic colours; a theme may retune them for contrast on its background */
  "--c-success": string;
  "--c-success-strong": string;
  "--c-danger": string;
  "--c-danger-strong": string;
  "--c-warning": string;
  /* surfaces ----------------------------------------------------------- */
  /** Opaque background for raised panels (modals, sticky bars) — these must
      not be translucent or the content behind shows through. */
  "--c-panel": string;
  /** Backdrop behind a modal. */
  "--scrim": string;
  "--surface-1": string;
  "--surface-2": string;
  "--surface-input": string;
  "--line": string;
  "--line-strong": string;
  /* form ---------------------------------------------------------------- */
  "--radius-control": string;
  "--radius-card": string;
  "--radius-surface": string;
  "--radius-pill": string;
  "--shadow-tile": string;
  "--shadow-pop": string;
  /* type ---------------------------------------------------------------- */
  "--font-ui": string;
  "--font-display": string;
  "--display-weight": string;
  "--display-stretch": string;
  "--display-tracking": string;
  "--display-transform": string;
  "--eyebrow-tracking": string;
  /* an optional overlay painted over the whole page (scanlines, grain) */
  "--texture": string;
  "--texture-opacity": string;
}

/**
 * The ids, as a union. Anything keyed by theme (the i18n label maps) is typed
 * against this, so adding or removing a theme without updating them is a
 * compile error rather than a silently missing translation.
 */
export type ThemeId = "classic" | "retro" | "minimal" | "clay" | "neon" | "paper";

export interface Theme {
  id: ThemeId;
  /** English label; the German one lives in the i18n dictionary. */
  label: string;
  /** Two colours used to draw the theme's swatch in the picker. */
  swatch: [string, string];
  /** Light themes need dark scrims and different image treatment. */
  scheme: "dark" | "light";
  /** Google Fonts family names to load when this theme is selected. */
  fonts: string[];
  vars: ThemeVars;
}

/** Shared defaults so each theme only states what makes it different. */
const base = {
  "--c-success": "34 197 94",
  "--c-success-strong": "22 163 74",
  "--c-danger": "239 68 68",
  "--c-danger-strong": "220 38 38",
  "--c-warning": "217 119 6",
  "--radius-pill": "999px",
  "--texture": "none",
  "--texture-opacity": "0",
  "--display-transform": "none",
  "--eyebrow-tracking": "0.16em",
} as const;

const darkSurfaces = {
  "--surface-1": "rgb(var(--c-ink) / 0.05)",
  "--surface-2": "rgb(var(--c-ink) / 0.09)",
  "--surface-input": "rgb(0 0 0 / 0.25)",
  "--line": "rgb(var(--c-ink) / 0.12)",
  "--line-strong": "rgb(var(--c-ink) / 0.22)",
} as const;

const lightSurfaces = {
  "--surface-1": "rgb(var(--c-ink) / 0.04)",
  "--surface-2": "rgb(var(--c-ink) / 0.08)",
  "--surface-input": "rgb(255 255 255 / 0.75)",
  "--line": "rgb(var(--c-ink) / 0.14)",
  "--line-strong": "rgb(var(--c-ink) / 0.26)",
} as const;

export const THEMES: Theme[] = [
  {
    id: "classic",
    label: "Classic",
    swatch: ["#060ce9", "#f5c518"],
    scheme: "dark",
    fonts: ["Archivo:wdth,wght@62..125,400..900"],
    vars: {
      ...base,
      ...darkSurfaces,
      "--c-board": "6 12 233",
      "--c-board-dark": "4 10 141",
      "--c-accent": "212 160 23",
      "--c-accent-light": "245 197 24",
      "--c-on-board": "245 197 24",
      "--c-ink": "255 255 255",
      "--c-on-accent": "0 0 0",
      "--bg": "radial-gradient(circle at 50% -10%, #0a1bd6 0%, #04063f 55%, #02021f 100%)",
      "--c-panel": "#0a0e45",
      "--scrim": "rgb(0 0 0 / 0.72)",
      "--radius-control": "0.5rem",
      "--radius-card": "0.875rem",
      "--radius-surface": "1.25rem",
      "--shadow-tile":
        "0 1px 0 0 rgb(255 255 255 / 0.08) inset, 0 6px 18px -8px rgb(0 0 0 / 0.6)",
      "--shadow-pop": "0 24px 60px -20px rgb(0 0 0 / 0.7)",
      "--font-ui": "Archivo, ui-sans-serif, system-ui, sans-serif",
      "--font-display": "Archivo, ui-sans-serif, system-ui, sans-serif",
      "--display-weight": "800",
      "--display-stretch": "115%",
      "--display-tracking": "-0.02em",
    },
  },

  {
    id: "retro",
    label: "Retro arcade",
    swatch: ["#0b0f0a", "#39ff14"],
    scheme: "dark",
    fonts: ["VT323", "Share+Tech+Mono"],
    vars: {
      ...base,
      ...darkSurfaces,
      "--c-board": "10 26 12",
      "--c-board-dark": "6 16 8",
      "--c-accent": "57 255 20",
      "--c-accent-light": "150 255 120",
      "--c-on-board": "150 255 120",
      "--c-ink": "222 255 214",
      "--c-on-accent": "3 12 3",
      "--bg": "#050b05",
      "--c-panel": "#0a140a",
      "--scrim": "rgb(0 0 0 / 0.82)",
      "--c-warning": "255 209 71",
      "--c-danger": "255 61 61",
      "--c-danger-strong": "214 24 24",
      "--surface-input": "rgb(0 0 0 / 0.5)",
      // Hard edges and hard shadows: a CRT cabinet has no rounded corners.
      "--radius-control": "0px",
      "--radius-card": "0px",
      "--radius-surface": "0px",
      "--radius-pill": "0px",
      "--shadow-tile": "0 0 0 2px rgb(57 255 20 / 0.35), 0 0 18px -2px rgb(57 255 20 / 0.35)",
      "--shadow-pop": "0 0 0 2px rgb(57 255 20 / 0.5), 0 0 40px -4px rgb(57 255 20 / 0.45)",
      "--font-ui": "'Share Tech Mono', ui-monospace, monospace",
      "--font-display": "VT323, 'Share Tech Mono', ui-monospace, monospace",
      "--display-weight": "400",
      "--display-stretch": "100%",
      "--display-tracking": "0.02em",
      "--display-transform": "uppercase",
      "--eyebrow-tracking": "0.3em",
      // Scanlines, drawn rather than downloaded.
      "--texture":
        "repeating-linear-gradient(to bottom, rgb(0 0 0 / 0.5) 0px, rgb(0 0 0 / 0.5) 1px, transparent 1px, transparent 3px)",
      "--texture-opacity": "0.55",
    },
  },

  {
    id: "minimal",
    label: "Minimal",
    swatch: ["#fafafa", "#111111"],
    scheme: "light",
    fonts: ["Inter:wght@300..700"],
    vars: {
      ...base,
      ...lightSurfaces,
      "--c-board": "244 244 245",
      "--c-board-dark": "228 228 231",
      "--c-accent": "24 24 27",
      "--c-accent-light": "24 24 27",
      "--c-on-board": "24 24 27",
      "--c-ink": "24 24 27",
      "--c-on-accent": "255 255 255",
      "--bg": "#fbfbfc",
      "--c-panel": "#ffffff",
      "--scrim": "rgb(24 24 27 / 0.45)",
      "--c-warning": "180 83 9",
      // Nothing floats. Hairlines do all the separating.
      "--radius-control": "0.375rem",
      "--radius-card": "0.5rem",
      "--radius-surface": "0.625rem",
      "--shadow-tile": "inset 0 0 0 1px rgb(24 24 27 / 0.1)",
      "--shadow-pop": "0 1px 2px rgb(24 24 27 / 0.06), 0 8px 24px -12px rgb(24 24 27 / 0.18)",
      "--font-ui": "Inter, ui-sans-serif, system-ui, sans-serif",
      "--font-display": "Inter, ui-sans-serif, system-ui, sans-serif",
      "--display-weight": "600",
      "--display-stretch": "100%",
      "--display-tracking": "-0.035em",
      "--eyebrow-tracking": "0.08em",
    },
  },

  {
    id: "clay",
    label: "Claymation",
    swatch: ["#ffd9c0", "#ff7a5c"],
    scheme: "light",
    fonts: ["Baloo+2:wght@400..800"],
    vars: {
      ...base,
      ...lightSurfaces,
      "--c-board": "255 173 148",
      "--c-board-dark": "247 143 114",
      "--c-accent": "255 122 92",
      "--c-accent-light": "176 66 42",
      "--c-on-board": "92 40 28",
      "--c-ink": "77 44 36",
      "--c-on-accent": "255 255 255",
      "--bg": "radial-gradient(circle at 20% 0%, #fff1e6 0%, #ffe3d3 45%, #ffd6c4 100%)",
      "--c-panel": "#fff6ef",
      "--scrim": "rgb(77 44 36 / 0.45)",
      "--c-success": "76 201 140",
      "--c-success-strong": "44 173 112",
      "--c-danger": "255 107 107",
      "--c-danger-strong": "232 74 74",
      "--surface-input": "rgb(255 255 255 / 0.85)",
      // Everything is a soft extruded lump.
      "--radius-control": "1rem",
      "--radius-card": "1.5rem",
      "--radius-surface": "2rem",
      "--shadow-tile":
        "0 6px 0 0 rgb(180 96 72 / 0.35), inset 0 3px 6px rgb(255 255 255 / 0.55)",
      "--shadow-pop":
        "0 12px 0 0 rgb(180 96 72 / 0.25), 0 24px 44px -18px rgb(120 60 40 / 0.45)",
      "--font-ui": "'Baloo 2', ui-rounded, system-ui, sans-serif",
      "--font-display": "'Baloo 2', ui-rounded, system-ui, sans-serif",
      "--display-weight": "800",
      "--display-stretch": "100%",
      "--display-tracking": "0em",
      "--eyebrow-tracking": "0.12em",
    },
  },

  {
    id: "neon",
    label: "Neon",
    swatch: ["#12002e", "#00f0ff"],
    scheme: "dark",
    fonts: ["Orbitron:wght@500..900", "Rajdhani:wght@400..700"],
    vars: {
      ...base,
      ...darkSurfaces,
      "--c-board": "44 12 92",
      "--c-board-dark": "28 6 62",
      "--c-accent": "0 240 255",
      "--c-accent-light": "125 249 255",
      "--c-on-board": "125 249 255",
      "--c-ink": "236 233 255",
      "--c-on-accent": "6 2 20",
      "--bg": "radial-gradient(circle at 50% 0%, #2b0a5e 0%, #14022f 55%, #080118 100%)",
      "--c-panel": "#180536",
      "--scrim": "rgb(4 0 14 / 0.78)",
      "--c-danger": "255 45 149",
      "--c-danger-strong": "224 20 122",
      "--radius-control": "0.25rem",
      "--radius-card": "0.5rem",
      "--radius-surface": "0.75rem",
      "--shadow-tile": "0 0 0 1px rgb(0 240 255 / 0.35), 0 0 24px -4px rgb(0 240 255 / 0.5)",
      "--shadow-pop": "0 0 0 1px rgb(0 240 255 / 0.4), 0 0 60px -10px rgb(0 240 255 / 0.55)",
      "--font-ui": "Rajdhani, ui-sans-serif, system-ui, sans-serif",
      "--font-display": "Orbitron, ui-sans-serif, system-ui, sans-serif",
      "--display-weight": "800",
      "--display-stretch": "100%",
      "--display-tracking": "0.04em",
      "--display-transform": "uppercase",
      "--eyebrow-tracking": "0.28em",
    },
  },

  {
    id: "paper",
    label: "Paper",
    swatch: ["#f6f1e4", "#8c2f24"],
    scheme: "light",
    fonts: ["Playfair+Display:wght@500..800", "Lora:wght@400..600"],
    vars: {
      ...base,
      ...lightSurfaces,
      "--c-board": "244 236 220",
      "--c-board-dark": "231 219 197",
      "--c-accent": "140 47 36",
      "--c-accent-light": "140 47 36",
      "--c-on-board": "140 47 36",
      "--c-ink": "45 38 32",
      "--c-on-accent": "252 249 242",
      "--bg": "#f7f2e7",
      "--c-panel": "#fffdf7",
      "--scrim": "rgb(45 38 32 / 0.45)",
      "--c-warning": "161 98 7",
      "--surface-input": "rgb(255 253 247 / 0.9)",
      "--radius-control": "0.25rem",
      "--radius-card": "0.375rem",
      "--radius-surface": "0.5rem",
      "--shadow-tile": "inset 0 0 0 1px rgb(45 38 32 / 0.12)",
      "--shadow-pop": "0 2px 4px rgb(45 38 32 / 0.08), 0 18px 40px -20px rgb(45 38 32 / 0.35)",
      "--font-ui": "Lora, Georgia, serif",
      "--font-display": "'Playfair Display', Georgia, serif",
      "--display-weight": "700",
      "--display-stretch": "100%",
      "--display-tracking": "-0.01em",
      "--eyebrow-tracking": "0.2em",
      // A faint fibrous grain, generated rather than fetched.
      "--texture":
        "repeating-radial-gradient(circle at 17% 29%, rgb(120 96 60 / 0.06) 0 1px, transparent 1px 3px), repeating-radial-gradient(circle at 71% 63%, rgb(120 96 60 / 0.05) 0 1px, transparent 1px 4px)",
      "--texture-opacity": "1",
    },
  },
];

export const DEFAULT_THEME = "classic";

export function themeById(id: string): Theme {
  return THEMES.find((t) => t.id === id) ?? THEMES[0];
}

export function isThemeId(id: unknown): id is ThemeId {
  return typeof id === "string" && THEMES.some((t) => t.id === id);
}
